import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import chatStyles from './HexChat.module.css';
import {
    HEX_REFINEMENTS,
    createHexSession,
    streamHexChat,
    streamHexRefinement,
    submitHexCapabilityRequest,
} from '../../api/hex';
import {isLocalDevelopment, PRISMAX_HEX_URL} from '../../configs/config';
import {buildSdkDownloadExample} from './apiPackageExample';

const SUGGESTIONS = [
    {label: 'Find similar episodes with quality score 90+', refinement: HEX_REFINEMENTS.quality90Plus},
    {label: 'Find more with different robot arm', refinement: HEX_REFINEMENTS.differentMachines},
];

function makeMessageId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function summarizeRecommendations(recommendations) {
    if (!Array.isArray(recommendations) || !recommendations.length) return null;
    return recommendations;
}

function normalizeSeeds(seedEpisodeIds) {
    if (!Array.isArray(seedEpisodeIds)) return [];
    return seedEpisodeIds.map((value) => Number(value)).filter((value) => Number.isFinite(value));
}

const REASON_LABELS = {
    same_upload_as_seed: 'same capture',
    same_task_and_machine_as_seed: 'same task + robot',
    same_task_as_seed: 'same task',
    same_scenario_as_seed: 'same scenario',
    same_environment_as_seed: 'same environment',
    task_match: 'task match',
    machine_match: 'robot match',
    machine_filter: 'robot filter',
    different_machine: 'different robot',
    explicit_lookup: 'lookup',
    catalog_sample: 'catalog sample',
    task_filter: 'task filter',
};

/** Shared with RoboticDataWorkspace “Hex recommended videos” strip. */
export function formatHexRecommendationReason(reason) {
    if (typeof reason !== 'string') return '';
    if (REASON_LABELS[reason]) return REASON_LABELS[reason];
    if (reason.startsWith('quality_ge_')) {
        const n = reason.slice('quality_ge_'.length);
        return `quality ≥ ${n}`;
    }
    if (reason.startsWith('quality_le_')) {
        const n = reason.slice('quality_le_'.length);
        return `quality ≤ ${n}`;
    }
    if (reason.startsWith('text_match:')) {
        const q = reason.slice('text_match:'.length);
        return `matches “${q}”`;
    }
    if (reason.startsWith('task_')) {
        return `from task ${reason.slice('task_'.length)}`;
    }
    if (reason === 'untasked') {
        return 'from untasked seed';
    }
    return reason.replace(/_/g, ' ');
}

const INLINE_MD_REGEX = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

function renderInlineMarkdown(text) {
    if (typeof text !== 'string') return text;
    if (!text) return text;
    const parts = text.split(INLINE_MD_REGEX);
    return parts.map((part, idx) => {
        if (!part) return null;
        if (part.length >= 4 && part.startsWith('**') && part.endsWith('**')) {
            return <strong key={idx}>{part.slice(2, -2)}</strong>;
        }
        if (part.length >= 3 && part.startsWith('*') && part.endsWith('*')) {
            return <em key={idx}>{part.slice(1, -1)}</em>;
        }
        if (part.length >= 3 && part.startsWith('`') && part.endsWith('`')) {
            return <code key={idx}>{part.slice(1, -1)}</code>;
        }
        return part;
    });
}

const DIRECT_DOWNLOAD_MAX_EPISODES = 10;

/** Hex API caps seed_episode_ids at 200 per request. */
const HEX_MAX_SEED_EPISODE_IDS = 200;

function seedsForHexRequest(seedEpisodeIds) {
    const normalized = normalizeSeeds(seedEpisodeIds);
    if (normalized.length <= HEX_MAX_SEED_EPISODE_IDS) {
        return normalized;
    }
    return normalized.slice(0, HEX_MAX_SEED_EPISODE_IDS);
}

// Matches "create api package", "api download", "api downlod", "download via api", etc.
const API_PACKAGE_CHAT_INTENT = /\b(?:(?:create|make|build|set\s*up)\s+(?:an?\s+)?api\s*pak?c?ages?|api\s*pak?c?age\s+(?:for|from|with)\s+(?:my\s+)?(?:selection|episodes?|seeds?)|api\s+(?:downl?oad|downlod)(?:\s+(?:these|my\s+selection|episodes?|seeds?))?|(?:downl?oad|downlod)\s+(?:via|with|using)?\s*(?:the\s+)?api(?:\s*key)?|download\s+(?:these|my\s+selection|selected)\s+(?:via|with|using)\s+(?:the\s+)?api)\b/i;

export function isApiPackageChatIntent(text) {
    return API_PACKAGE_CHAT_INTENT.test(String(text || '').trim());
}

function apiPackageMessageFields(meta, fallbackText) {
    const ui = meta && meta.api_package_ui;
    if (!ui || !ui.kind) {
        return {text: fallbackText};
    }
    if (ui.kind === 'success' && ui.package_id) {
        const pid = String(ui.package_id).trim();
        return {
            text: fallbackText,
            apiPackageWidget: {
                package_id: pid,
                example: buildSdkDownloadExample(pid),
            },
        };
    }
    if (ui.kind === 'conflict' && Array.isArray(ui.conflicting_episode_ids) && ui.conflicting_episode_ids.length) {
        const dupCount = ui.conflicting_episode_ids.length;
        return {
            text: fallbackText || (
                `**${dupCount} episode${dupCount === 1 ? '' : 's'}** in your selection `
                + `${dupCount === 1 ? 'is' : 'are'} already in another API package. `
                + 'Use **Create new package with all selected** below.'
            ),
            apiPackageConflictWidget: {
                conflictingEpisodeIds: ui.conflicting_episode_ids,
            },
        };
    }
    return {text: fallbackText};
}

function HexChat({
    selectedEpisodeIds,
    gatewayToken,
    workspaceStyles,
    selectedEpisodeCount,
    onAddEpisodesToSelection,
    onRemoveEpisodesFromSelection,
    onTriggerDirectDownload,
    onCreateApiPackage,
    onClearSelection,
    onCopyText,
    isDirectDownloading,
    isCreatingApiPackage,
    onHexRecommendationsChange,
    onOpenApiAccessTab,
}) {
    const seeds = useMemo(() => normalizeSeeds(selectedEpisodeIds), [selectedEpisodeIds]);
    const seedsRef = useRef(seeds);
    useEffect(() => {
        seedsRef.current = seeds;
    }, [seeds]);

    const selectedSet = useMemo(() => new Set(seeds), [seeds]);

    const handleAddAll = useCallback((recommendations) => {
        if (typeof onAddEpisodesToSelection !== 'function') return;
        const ids = (recommendations || [])
            .map((r) => Number(r && r.episode_id))
            .filter((v) => Number.isFinite(v));
        if (!ids.length) return;
        onAddEpisodesToSelection(ids);
    }, [onAddEpisodesToSelection]);

    const handleAddOne = useCallback((episodeId) => {
        if (typeof onAddEpisodesToSelection !== 'function') return;
        const id = Number(episodeId);
        if (!Number.isFinite(id)) return;
        onAddEpisodesToSelection([id]);
    }, [onAddEpisodesToSelection]);

    const handleRemoveOne = useCallback((episodeId) => {
        if (typeof onRemoveEpisodesFromSelection !== 'function') return;
        const id = Number(episodeId);
        if (!Number.isFinite(id)) return;
        onRemoveEpisodesFromSelection([id]);
    }, [onRemoveEpisodesFromSelection]);

    const handleRemoveAll = useCallback((recommendations) => {
        if (typeof onRemoveEpisodesFromSelection !== 'function') return;
        const ids = (recommendations || [])
            .map((r) => Number(r && r.episode_id))
            .filter((v) => Number.isFinite(v));
        if (!ids.length) return;
        onRemoveEpisodesFromSelection(ids);
    }, [onRemoveEpisodesFromSelection]);

    const sessionRef = useRef({sessionId: null});
    const messageListRef = useRef(null);
    const inFlightRef = useRef(null);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [pending, setPending] = useState(false);
    /** Steps rendered live while the SSE stream is open (mirrors final message tool trace). */
    const [pendingToolTrace, setPendingToolTrace] = useState([]);
    /** Shown next to the typing dots before any tool_started event (e.g. first OpenAI round-trip). */
    const [pendingHint, setPendingHint] = useState(null);
    const [hexBackendHealth, setHexBackendHealth] = useState(null);

    useEffect(() => {
        if (!isLocalDevelopment) return undefined;
        let cancelled = false;
        fetch(`${PRISMAX_HEX_URL}/health`)
            .then((response) => response.json().catch(() => ({})))
            .then((data) => {
                if (!cancelled) setHexBackendHealth(data && typeof data === 'object' ? data : null);
            })
            .catch(() => {
                if (!cancelled) setHexBackendHealth({status: 'error'});
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const node = messageListRef.current;
        if (node) {
            node.scrollTop = node.scrollHeight;
        }
    }, [messages, pending, pendingToolTrace, pendingHint, isCreatingApiPackage]);

    useEffect(() => () => {
        if (inFlightRef.current) {
            inFlightRef.current.abort();
            inFlightRef.current = null;
        }
    }, []);

    const ensureSession = useCallback(async (signal) => {
        if (sessionRef.current.sessionId) {
            return sessionRef.current.sessionId;
        }
        const data = await createHexSession({
            seedEpisodeIds: seedsForHexRequest(seedsRef.current),
            token: gatewayToken,
            signal,
        });
        const sessionId = data && data.session_id;
        if (!sessionId) {
            throw new Error('Hex did not return a session id');
        }
        sessionRef.current = {sessionId};
        return sessionId;
    }, [gatewayToken]);

    const runHex = useCallback(async ({mode, message, refinement, label}) => {
        if (pending) return;
        if (!gatewayToken) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: 'You need to be signed in to chat with Hex.',
            }));
            return;
        }
        // Refinement chips ("more variation", "different robot arms", etc.) only make
        // sense relative to a seed set, so keep that gate. Free-form chat works without
        // seeds — Hex can answer info questions, run filter-based add_* tools, etc.
        if (mode !== 'chat' && !selectedEpisodeCount) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: 'Pick at least one sample episode above to use refinement shortcuts.',
            }));
            return;
        }

        const controller = new AbortController();
        inFlightRef.current = controller;

        const userText = mode === 'chat' ? message : label;
        setMessages((prev) => prev.concat({
            id: makeMessageId(),
            kind: 'user',
            text: userText,
        }));
        setPending(true);
        setPendingToolTrace([]);
        setPendingHint(mode === 'chat' ? 'Hex is thinking…' : `Hex is applying: ${label}`);

        let lastAssistant = null;
        let capabilityOffer = null;
        const toolEvents = [];

        const onEvent = (event) => {
            if (!event || !event.data) return;
            switch (event.type) {
                case 'session_ready':
                    break;
                case 'capability_request_offer':
                    capabilityOffer = event.data;
                    break;
                case 'tool_started': {
                    const display = event.data.display_name || `Running ${event.data.name || 'tool'}…`;
                    const row = {
                        id: event.data.tool_id || makeMessageId(),
                        name: event.data.name,
                        display,
                        status: 'running',
                    };
                    toolEvents.push(row);
                    setPendingToolTrace(toolEvents.map((t) => ({...t})));
                    break;
                }
                case 'tool_finished': {
                    const idx = toolEvents.findIndex((t) => t.id === event.data.tool_id);
                    if (idx >= 0) {
                        toolEvents[idx] = {
                            ...toolEvents[idx],
                            status: event.data.ok ? 'ok' : 'error',
                            summary: event.data.summary,
                            error: event.data.error,
                        };
                    }
                    setPendingToolTrace(toolEvents.map((t) => ({...t})));
                    break;
                }
                case 'assistant_message':
                    lastAssistant = event.data;
                    break;
                case 'error':
                    lastAssistant = {error: event.data && event.data.message};
                    break;
                case 'done':
                default:
                    break;
            }
        };

        try {
            const sessionId = await ensureSession(controller.signal);
            const seedsToSend = seedsForHexRequest(seedsRef.current);
            if (mode === 'chat') {
                await streamHexChat({
                    sessionId,
                    message,
                    seedEpisodeIds: seedsToSend,
                    token: gatewayToken,
                    signal: controller.signal,
                    onEvent,
                });
            } else {
                await streamHexRefinement({
                    sessionId,
                    refinement,
                    seedEpisodeIds: seedsToSend,
                    token: gatewayToken,
                    signal: controller.signal,
                    onEvent,
                });
            }

            if (lastAssistant && lastAssistant.error) {
                throw new Error(lastAssistant.error);
            }

            const reply = (lastAssistant && lastAssistant.reply) || 'Hex returned no reply text.';
            const recommendations = summarizeRecommendations(lastAssistant && lastAssistant.recommendations);
            const meta = (lastAssistant && lastAssistant.meta) || {};
            const offer = capabilityOffer || meta.capability_offer || null;
            const apiFields = apiPackageMessageFields(meta, reply);

            if (meta.focus_api_access_tab && typeof onOpenApiAccessTab === 'function') {
                onOpenApiAccessTab();
            }

            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'hex',
                ...apiFields,
                recommendations,
                meta,
                capabilityOffer: offer,
                tools: toolEvents.slice(),
            }));
            if (typeof onHexRecommendationsChange === 'function') {
                onHexRecommendationsChange(
                    recommendations && recommendations.length ? recommendations : [],
                );
            }
        } catch (err) {
            if (controller.signal.aborted) return;
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: (err && err.message) || 'Hex request failed.',
            }));
        } finally {
            if (inFlightRef.current === controller) {
                inFlightRef.current = null;
            }
            setPending(false);
            setPendingToolTrace([]);
            setPendingHint(null);
        }
    }, [ensureSession, gatewayToken, onOpenApiAccessTab, pending, selectedEpisodeCount, onHexRecommendationsChange]);

    const handleCapabilityConsent = useCallback(async (messageId, approve) => {
        const target = messages.find((m) => m.id === messageId);
        const offer = target && target.capabilityOffer;
        if (!offer || !offer.pending_token || target.capabilityOfferResolved) {
            return;
        }
        if (!gatewayToken) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: 'Sign in to raise a request to the Prismax team.',
            }));
            return;
        }
        try {
            const sessionId = await ensureSession();
            const result = await submitHexCapabilityRequest({
                sessionId,
                pendingToken: offer.pending_token,
                approve,
                token: gatewayToken,
            });
            setMessages((prev) => prev.map((m) => (
                m.id === messageId
                    ? {...m, capabilityOfferResolved: approve ? 'approved' : 'declined'}
                    : m
            )));
            if (approve) {
                setMessages((prev) => prev.concat({
                    id: makeMessageId(),
                    kind: 'hex',
                    text: (result && result.message)
                        || "Your request was sent to the Prismax team — they'll review whether we can add or improve this in the catalog.",
                }));
            }
        } catch (err) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: (err && err.message) || 'Could not record your request.',
            }));
        }
    }, [ensureSession, gatewayToken, messages]);

    const handleSuggestion = useCallback((suggestion) => {
        runHex({mode: 'refine', refinement: suggestion.refinement, label: suggestion.label});
    }, [runHex]);

    const handleAddSamplesFromOffer = useCallback(() => {
        runHex({mode: 'chat', message: 'yes add 10 episodes to my selection'});
    }, [runHex]);

    const appendApiPackageSuccess = useCallback((data) => {
        const pid = data && data.package_id ? String(data.package_id).trim() : '';
        if (!pid) return false;
        const example = buildSdkDownloadExample(pid);
        setMessages((prev) => prev.concat({
            id: makeMessageId(),
            kind: 'hex',
            text: '**Package ready.** Copy the package ID or use the SDK commands below to download it.',
            apiPackageWidget: {package_id: pid, example},
        }));
        return true;
    }, []);

    const handleCreateApiPackageClick = useCallback(async (userText) => {
        if (typeof onCreateApiPackage !== 'function') return;
        const label = (typeof userText === 'string' && userText.trim())
            ? userText.trim()
            : `Create package (${selectedEpisodeCount})`;
        setMessages((prev) => prev.concat({
            id: makeMessageId(),
            kind: 'user',
            text: label,
        }));
        if (typeof onOpenApiAccessTab === 'function') {
            onOpenApiAccessTab();
        }
        const result = await onCreateApiPackage();
        if (result && result.conflict && Array.isArray(result.conflictingEpisodeIds)) {
            const dupCount = result.conflictingEpisodeIds.length;
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'hex',
                text: `**${dupCount} episode${dupCount === 1 ? '' : 's'}** in your selection ${dupCount === 1 ? 'is' : 'are'} already in another package. You can still create a **new** package with them for SDK download.`,
                apiPackageConflictWidget: {
                    conflictingEpisodeIds: result.conflictingEpisodeIds,
                },
            }));
            return;
        }
        if (result && result.error) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: result.error || 'Could not create the API package.',
            }));
            return;
        }
        const data = result && result.data ? result.data : result;
        if (!appendApiPackageSuccess(data)) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: 'Could not create the API package.',
            }));
        }
    }, [appendApiPackageSuccess, onCreateApiPackage, onOpenApiAccessTab, selectedEpisodeCount]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        const trimmed = input.trim();
        if (!trimmed) return;
        setInput('');
        if (isApiPackageChatIntent(trimmed)) {
            if (!selectedEpisodeCount) {
                setMessages((prev) => prev.concat(
                    {id: makeMessageId(), kind: 'user', text: trimmed},
                    {
                        id: makeMessageId(),
                        kind: 'hex',
                        text: 'Select episodes in the catalog first, then use **Create package** in the suggested actions below or ask again.',
                    },
                ));
                if (typeof onOpenApiAccessTab === 'function') {
                    onOpenApiAccessTab();
                }
                return;
            }
            handleCreateApiPackageClick(trimmed);
            return;
        }
        runHex({mode: 'chat', message: trimmed});
    }, [handleCreateApiPackageClick, input, onOpenApiAccessTab, runHex, selectedEpisodeCount]);

    const handleApiPackageConflictChoice = useCallback(async (messageId, choice) => {
        if (typeof onCreateApiPackage !== 'function') return;
        const conflictMsg = messages.find((m) => m.id === messageId);
        const conflicting = conflictMsg?.apiPackageConflictWidget?.conflictingEpisodeIds;
        if (!Array.isArray(conflicting) || !conflicting.length) return;

        setMessages((prev) => prev.map((m) => (
            m.id === messageId
                ? {...m, apiPackageConflictWidget: null, apiPackageConflictResolved: choice}
                : m
        )));

        if (choice === 'remove') {
            if (typeof onRemoveEpisodesFromSelection === 'function') {
                onRemoveEpisodesFromSelection(conflicting);
            }
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'hex',
                text: `Removed ${conflicting.length} episode${conflicting.length === 1 ? '' : 's'} from your selection (they stay in your existing API package${conflicting.length === 1 ? '' : 's'}). Pick other episodes if you want a new package.`,
            }));
            return;
        }

        const allSelectedIds = seedsRef.current.slice();
        const result = await onCreateApiPackage({
            episodeIds: allSelectedIds,
            allowAlreadyPackaged: true,
        });

        if (result && result.conflict) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: result.message || 'Could not create the API package.',
            }));
            return;
        }
        if (result && result.error) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: result.error,
            }));
            return;
        }
        const data = result && result.data ? result.data : null;
        if (!appendApiPackageSuccess(data)) {
            setMessages((prev) => prev.concat({
                id: makeMessageId(),
                kind: 'error',
                text: 'Could not create the API package.',
            }));
        }
    }, [appendApiPackageSuccess, messages, onCreateApiPackage, onRemoveEpisodesFromSelection]);

    const ws = workspaceStyles || {};
    const inputDisabled = pending || !gatewayToken || isCreatingApiPackage;
    // Refinement suggestions only make sense relative to a seed set; chat itself doesn't.
    const refinementsDisabled = inputDisabled || !selectedEpisodeCount;
    const placeholder = !gatewayToken
        ? 'Sign in to chat with Hex'
        : !selectedEpisodeCount
            ? 'Ask Hex anything — e.g. "how many Piper episodes are there?"'
            : 'Ask Hex about your dataset…';

    return (
        <div className={chatStyles.chatRoot}>
            <div ref={messageListRef} className={chatStyles.messageList}>
                <div className={`${chatStyles.bubble} ${chatStyles.hexBubble} ${chatStyles.greetingBubble}`}>
                    {selectedEpisodeCount ? (
                        <p>
                            I can see you've selected <strong>{selectedEpisodeCount} sample episode{selectedEpisodeCount === 1 ? '' : 's'}</strong> across the tasks above. I can find similar videos, download in the browser, or create a package for SDK download.
                        </p>
                    ) : (
                        <>
                            <p>Ask me anything about the catalog — counts, tasks, robot arms, recent uploads, your own download history. I can also pull episodes by task, robot, quality, or recency.</p>
                            <p>Pick sample episodes above to unlock browser or SDK downloads.</p>
                            {isLocalDevelopment ? (
                                <p className={chatStyles.hexBackendHint}>
                                    Hex backend: <code>{PRISMAX_HEX_URL}</code>
                                    {hexBackendHealth && hexBackendHealth.build ? (
                                        <> · build <code>{hexBackendHealth.build}</code></>
                                    ) : null}
                                    {hexBackendHealth && hexBackendHealth.db && hexBackendHealth.db.ok === false ? (
                                        <> · <strong>DB offline — {hexBackendHealth.db.error}</strong></>
                                    ) : null}
                                    {PRISMAX_HEX_URL.includes('run.app') ? (
                                        <> · <strong>cloud Hex — quality auto-filter bug still present; use 127.0.0.1:8090</strong></>
                                    ) : null}
                                    {hexBackendHealth && hexBackendHealth.catalog_fast_path !== true ? (
                                        <> · <strong>outdated Hex — restart local service on :8090</strong></>
                                    ) : null}
                                </p>
                            ) : null}
                        </>
                    )}
                    <div className={chatStyles.greetingChips}>
                        {SUGGESTIONS.map((suggestion) => (
                            <button
                                key={suggestion.refinement}
                                type="button"
                                className={chatStyles.actionSecondary}
                                onClick={() => handleSuggestion(suggestion)}
                                disabled={refinementsDisabled}
                                title={!selectedEpisodeCount ? 'Select sample episodes above to use refinement shortcuts' : suggestion.label}
                            >
                                {suggestion.label}
                            </button>
                        ))}
                    </div>
                </div>
                {messages.map((msg) => {
                        if (msg.kind === 'user') {
                            return (
                                <div key={msg.id} className={`${chatStyles.bubble} ${chatStyles.userBubble}`}>
                                    {renderInlineMarkdown(msg.text)}
                                </div>
                            );
                        }
                        if (msg.kind === 'error') {
                            return (
                                <div key={msg.id} className={`${chatStyles.bubble} ${chatStyles.errorBubble}`}>
                                    {renderInlineMarkdown(msg.text)}
                                </div>
                            );
                        }
                        return (
                            <div key={msg.id} className={`${chatStyles.bubble} ${chatStyles.hexBubble}`}>
                                {Array.isArray(msg.tools) && msg.tools.length > 0 && (
                                    <div className={chatStyles.toolTrace}>
                                        {msg.tools.map((tool) => (
                                            <div
                                                key={tool.id || `${tool.name}-${tool.display}`}
                                                className={`${chatStyles.toolTraceLine} ${
                                                    tool.status === 'error' ? chatStyles.toolTraceErr : ''
                                                }`}
                                            >
                                                <span className={chatStyles.toolDot}/>
                                                <span className={chatStyles.toolDisplay}>{tool.display}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {msg.text.split('\n').filter(Boolean).map((line, idx) => (
                                    <p key={idx}>{renderInlineMarkdown(line)}</p>
                                ))}
                                {msg.meta && msg.meta.limit_reached && (
                                    <span className={chatStyles.engineTag}>tool limit reached</span>
                                )}
                                {msg.capabilityOfferResolved === 'declined' ? (
                                    <p className={chatStyles.actionWidgetHint}>
                                        Got it.
                                    </p>
                                ) : null}
                                {msg.apiPackageConflictWidget
                                    && Array.isArray(msg.apiPackageConflictWidget.conflictingEpisodeIds)
                                    && msg.apiPackageConflictWidget.conflictingEpisodeIds.length
                                    && !msg.apiPackageConflictResolved ? (
                                    <div className={chatStyles.apiPackageConflictWrap}>
                                        <p className={chatStyles.apiPackageConflictIds}>
                                            Episode IDs: {msg.apiPackageConflictWidget.conflictingEpisodeIds.join(', ')}
                                        </p>
                                        <div className={chatStyles.apiPackageConflictActions}>
                                            <button
                                                type="button"
                                                className={chatStyles.actionPrimary}
                                                disabled={!!isCreatingApiPackage}
                                                onClick={() => void handleApiPackageConflictChoice(msg.id, 'create')}
                                            >
                                                Create new package with all selected
                                            </button>
                                            <button
                                                type="button"
                                                className={chatStyles.actionSecondary}
                                                disabled={!!isCreatingApiPackage}
                                                onClick={() => void handleApiPackageConflictChoice(msg.id, 'remove')}
                                            >
                                                Remove from selection (cancel package)
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                                {msg.apiPackageConflictResolved ? (
                                    <p className={chatStyles.actionWidgetHint}>
                                        {msg.apiPackageConflictResolved === 'remove'
                                            ? 'Removed from selection.'
                                            : 'New package created with all selected episodes.'}
                                    </p>
                                ) : null}
                                {msg.apiPackageWidget && msg.apiPackageWidget.package_id ? (
                                    <div className={chatStyles.apiPackageWidgetWrap}>
                                        <div className={chatStyles.apiPackageSuccess}>
                                            <div className={chatStyles.apiPackageSuccessLabel}>Package</div>
                                            <code className={chatStyles.apiPackageId}>{msg.apiPackageWidget.package_id}</code>
                                            <div className={chatStyles.apiPackageActions}>
                                                {typeof onCopyText === 'function' ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={chatStyles.apiPackageCopyButton}
                                                            onClick={() => onCopyText(msg.apiPackageWidget.package_id, 'Package ID copied.')}
                                                        >
                                                            Copy package ID
                                                        </button>
                                                        {msg.apiPackageWidget.example ? (
                                                            <button
                                                                type="button"
                                                                className={chatStyles.apiPackageCopyButton}
                                                                onClick={() => onCopyText(msg.apiPackageWidget.example, 'Download example copied.')}
                                                            >
                                                                Copy example
                                                            </button>
                                                        ) : null}
                                                    </>
                                                ) : null}
                                            </div>
                                            {msg.apiPackageWidget.example ? (
                                                <pre className={chatStyles.apiPackageExample}>{msg.apiPackageWidget.example}</pre>
                                            ) : (
                                                <p className={chatStyles.actionWidgetHint}>
                                                    Use the SDK tab to create a download key and run these commands.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                                {msg.recommendations && msg.recommendations.length > 0 && (() => {
                                    const newOnes = msg.recommendations.filter(
                                        (r) => !selectedSet.has(Number(r.episode_id))
                                    );
                                    const alreadyOnes = msg.recommendations.filter(
                                        (r) => selectedSet.has(Number(r.episode_id))
                                    );
                                    return (
                                        <>
                                            <div className={chatStyles.recommendationsHeader}>
                                                <span>{msg.recommendations.length} matching episode{msg.recommendations.length === 1 ? '' : 's'}</span>
                                                {newOnes.length > 0 && typeof onAddEpisodesToSelection === 'function' && (
                                                    <button
                                                        type="button"
                                                        className={chatStyles.addAllButton}
                                                        onClick={() => handleAddAll(newOnes)}
                                                    >
                                                        + Add all to selection ({newOnes.length})
                                                    </button>
                                                )}
                                                {alreadyOnes.length > 0 && typeof onRemoveEpisodesFromSelection === 'function' && (
                                                    <button
                                                        type="button"
                                                        className={chatStyles.removeAllButton}
                                                        onClick={() => handleRemoveAll(alreadyOnes)}
                                                        title="Remove these episodes from your selection"
                                                    >
                                                        − Remove from selection ({alreadyOnes.length})
                                                    </button>
                                                )}
                                            </div>
                                            <div className={chatStyles.recommendationList}>
                                                {msg.recommendations.map((rec) => {
                                                    const id = Number(rec.episode_id);
                                                    const alreadySelected = selectedSet.has(id);
                                                    const reasonText = Array.isArray(rec.reasons)
                                                        ? rec.reasons.slice(0, 3).map(formatHexRecommendationReason).filter(Boolean).join(' · ')
                                                        : '';
                                                    return (
                                                        <div key={rec.episode_id} className={chatStyles.recommendation}>
                                                            <span className={chatStyles.recEpisode}>#{rec.episode_id}</span>
                                                            <span className={chatStyles.recBody}>
                                                                {rec.scenario && (
                                                                    <span className={chatStyles.recScenario}>{rec.scenario}</span>
                                                                )}
                                                                {reasonText && (
                                                                    <span className={chatStyles.recReasons}>{reasonText}</span>
                                                                )}
                                                            </span>
                                                            {Number.isFinite(rec.quality_score) && (
                                                                <span className={chatStyles.recQuality} title="QA quality score">Q {rec.quality_score}</span>
                                                            )}
                                                            {alreadySelected && typeof onRemoveEpisodesFromSelection === 'function' ? (
                                                                <button
                                                                    type="button"
                                                                    className={chatStyles.recRemoveButton}
                                                                    onClick={() => handleRemoveOne(rec.episode_id)}
                                                                    aria-label={`Remove episode #${rec.episode_id} from selection`}
                                                                    title="Click to remove from selection"
                                                                >
                                                                    <span className={chatStyles.recRemoveCheck}>✓</span>
                                                                    <span className={chatStyles.recRemoveMinus}>−</span>
                                                                </button>
                                                            ) : alreadySelected ? (
                                                                <span className={chatStyles.recSelected} title="already in your selection">✓</span>
                                                            ) : typeof onAddEpisodesToSelection === 'function' ? (
                                                                <button
                                                                    type="button"
                                                                    className={chatStyles.recAddButton}
                                                                    onClick={() => handleAddOne(rec.episode_id)}
                                                                    title="Add to selection"
                                                                >
                                                                    +
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    );
                                })()}
                                {msg.capabilityOffer
                                    && msg.capabilityOffer.pending_token
                                    && !msg.capabilityOfferResolved
                                    && (
                                        msg.capabilityOffer.reason_code === 'weak_task_match'
                                        || !(msg.recommendations && msg.recommendations.length)
                                    ) ? (
                                    <div className={chatStyles.apiPackageConflictWrap}>
                                        <p className={chatStyles.actionWidgetHint}>
                                            {msg.capabilityOffer.reason_code === 'weak_task_match'
                                                ? 'These might not be what you asked for.'
                                                : 'We don\u2019t have this in your catalog yet.'}
                                        </p>
                                        <p className={chatStyles.actionWidgetHint}>
                                            {msg.capabilityOffer.reason_code === 'weak_task_match'
                                                ? 'Raise to admin and we\u2019ll send your request to the Prismax team so they can review whether this task should exist or be matched differently.'
                                                : 'Raise to admin and we\u2019ll send your request to the Prismax team so they can look into adding it to the catalog.'}
                                        </p>
                                        <div className={chatStyles.apiPackageConflictActions}>
                                            {msg.capabilityOffer.reason_code === 'weak_task_match'
                                                && !(msg.recommendations && msg.recommendations.length) ? (
                                                <button
                                                    type="button"
                                                    className={chatStyles.actionPrimary}
                                                    disabled={pending}
                                                    onClick={handleAddSamplesFromOffer}
                                                >
                                                    Add 10 samples
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className={
                                                    msg.capabilityOffer.reason_code === 'weak_task_match'
                                                    && !(msg.recommendations && msg.recommendations.length)
                                                        ? chatStyles.actionSecondary
                                                        : chatStyles.actionPrimary
                                                }
                                                disabled={pending}
                                                onClick={() => void handleCapabilityConsent(msg.id, true)}
                                            >
                                                Raise to admin
                                            </button>
                                            <button
                                                type="button"
                                                className={chatStyles.actionSecondary}
                                                disabled={pending}
                                                onClick={() => void handleCapabilityConsent(msg.id, false)}
                                            >
                                                Dismiss
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                    {pending && (
                        <div className={`${chatStyles.bubble} ${chatStyles.hexBubble}`}>
                            {pendingToolTrace.length > 0 && (
                                <div className={chatStyles.toolTrace}>
                                    {pendingToolTrace.map((tool) => (
                                        <div
                                            key={tool.id || `${tool.name}-${tool.display}`}
                                            className={`${chatStyles.toolTraceLine} ${
                                                tool.status === 'error' ? chatStyles.toolTraceErr : ''
                                            } ${tool.status === 'running' ? chatStyles.toolTraceRunning : ''}`}
                                        >
                                            <span className={chatStyles.toolDot}/>
                                            <span className={chatStyles.toolDisplay}>{tool.display}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <span className={chatStyles.typing}>
                                <span/><span/><span/>
                                <span style={{marginLeft: 6}}>
                                    {pendingToolTrace.length > 0 ? 'Hex is thinking…' : (pendingHint || 'Hex is thinking…')}
                                </span>
                            </span>
                        </div>
                    )}
                    {isCreatingApiPackage && !pending && (
                        <div className={`${chatStyles.bubble} ${chatStyles.hexBubble}`}>
                            <span className={chatStyles.typing}>
                                <span/><span/><span/>
                                <span style={{marginLeft: 6}}>Hex is creating your package…</span>
                            </span>
                        </div>
                    )}
                </div>

            {selectedEpisodeCount > 0 && !pending && !input.trim() && !isCreatingApiPackage && (
                <div className={chatStyles.actionWidget}>
                    <div className={chatStyles.actionWidgetHeader}>
                        <span className={chatStyles.actionWidgetTitle}>Suggested next steps</span>
                        <span className={chatStyles.actionWidgetCount}>
                            {selectedEpisodeCount} episode{selectedEpisodeCount === 1 ? '' : 's'} selected
                        </span>
                    </div>
                    <div className={chatStyles.actionWidgetButtons}>
                        {typeof onTriggerDirectDownload === 'function' && (
                            <button
                                type="button"
                                className={chatStyles.actionPrimary}
                                onClick={onTriggerDirectDownload}
                                disabled={
                                    !!isDirectDownloading
                                    || !!isCreatingApiPackage
                                    || selectedEpisodeCount > DIRECT_DOWNLOAD_MAX_EPISODES
                                }
                                title={selectedEpisodeCount > DIRECT_DOWNLOAD_MAX_EPISODES
                                    ? `Browser download is capped at ${DIRECT_DOWNLOAD_MAX_EPISODES} episodes — create a package and use the SDK instead.`
                                    : 'Stream files directly to your browser'}
                            >
                                {isDirectDownloading ? 'Downloading…' : `Download in browser (${selectedEpisodeCount})`}
                            </button>
                        )}
                        {typeof onCreateApiPackage === 'function' && (
                            <button
                                type="button"
                                className={chatStyles.actionPrimary}
                                onClick={() => handleCreateApiPackageClick()}
                                disabled={!!isDirectDownloading || !!isCreatingApiPackage}
                                title="Save selected episodes as a package, then download it with the PrismaX SDK"
                            >
                                {isCreatingApiPackage ? 'Creating package…' : `Create package (${selectedEpisodeCount})`}
                            </button>
                        )}
                        {SUGGESTIONS.map((suggestion) => (
                            <button
                                key={`widget-${suggestion.refinement}`}
                                type="button"
                                className={chatStyles.actionSecondary}
                                onClick={() => handleSuggestion(suggestion)}
                                disabled={refinementsDisabled}
                                title={refinementsDisabled ? 'Wait for the current request to finish' : suggestion.label}
                            >
                                {suggestion.label}
                            </button>
                        ))}
                        {typeof onClearSelection === 'function' && (
                            <button
                                type="button"
                                className={chatStyles.actionGhost}
                                onClick={onClearSelection}
                                title="Clear all selected episodes"
                            >
                                Clear selection
                            </button>
                        )}
                    </div>
                    {selectedEpisodeCount > DIRECT_DOWNLOAD_MAX_EPISODES && (
                        <p className={chatStyles.actionWidgetHint}>
                            Browser download is capped at {DIRECT_DOWNLOAD_MAX_EPISODES} episodes. For {selectedEpisodeCount}, create a package and download it with the SDK.
                        </p>
                    )}
                </div>
            )}

            <form className={ws.hexInputWrap || ''} onSubmit={handleSubmit}>
                <input
                    type="text"
                    className={ws.hexInput || ''}
                    placeholder={placeholder}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    disabled={inputDisabled}
                    aria-label="Message Hex"
                />
                <button
                    type="submit"
                    className={ws.hexSendButton || ''}
                    disabled={inputDisabled || !input.trim()}
                    aria-label="Send message"
                >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M2 8h11" strokeLinecap="round"/>
                        <path d="M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </form>
        </div>
    );
}

export default HexChat;
